import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";

/* ---------- FadeIn: scroll-triggered entrance ---------- */
export const FadeIn = ({ children, delay = 0, y = 28, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

/* ---------- SplitText: word-by-word reveal (reactbits style) ---------- */
export const SplitText = ({ text, className = "", delay = 0 }) => {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => (
        <span key={i} style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}>
          <motion.span
            style={{ display: "inline-block", willChange: "transform" }}
            initial={{ y: "110%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            transition={{ duration: 0.6, delay: delay + i * 0.055, ease: [0.22, 1, 0.36, 1] }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 ? <span>&nbsp;</span> : null}
        </span>
      ))}
    </span>
  );
};

/* ---------- CountUp: animated stat counter ---------- */
export const CountUp = ({ end, suffix = "", duration = 1.8, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = null;
    let raf;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * end));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  return (
    <span ref={ref} className={className}>
      {val}
      {suffix}
    </span>
  );
};

/* ---------- SpotlightCard: mouse-follow glow (reactbits style) ---------- */
export const SpotlightCard = ({ children, className = "" }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: -300, y: -300 });
  const [hover, setHover] = useState(false);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`relative overflow-hidden ${className}`}
    >
      <div
        className="pointer-events-none absolute inset-0 transition-opacity duration-500"
        style={{
          opacity: hover ? 1 : 0,
          background: `radial-gradient(420px circle at ${pos.x}px ${pos.y}px, rgba(240,163,34,0.12), transparent 65%)`,
        }}
      />
      {children}
    </div>
  );
};

/* ---------- TiltCard: subtle 3D tilt on hover ---------- */
export const TiltCard = ({ children, className = "" }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 180, damping: 20 });
  const sry = useSpring(ry, { stiffness: 180, damping: 20 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 7);
    rx.set(-py * 7);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 900 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ---------- HexGrid: honeycomb SVG background pattern ---------- */
export const HexPattern = ({ className = "", opacity = 0.08 }) => (
  <svg className={`pointer-events-none absolute inset-0 h-full w-full ${className}`} style={{ opacity }} aria-hidden="true">
    <defs>
      <pattern id="hexes" width="56" height="97" patternUnits="userSpaceOnUse" patternTransform="scale(1.1)">
        <path
          d="M28 0 L56 16 L56 48 L28 64 L0 48 L0 16 Z M28 64 L56 80 L56 112 L28 128 L0 112 L0 80 Z"
          fill="none"
          stroke="#E8930C"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#hexes)" />
  </svg>
);

/* ---------- Eyebrow: section label ---------- */
export const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2.5 rounded-full border border-[#F0A322]/35 bg-[#F0A322]/[0.08] px-4 py-1.5">
    <span className="h-1.5 w-1.5 rounded-full bg-[#E8930C] animate-pulse" />
    <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#B45309]">{children}</span>
  </div>
);

/* ---------- BlurText: blur-in word reveal on scroll (reactbits style) ---------- */
export const BlurText = ({ text, className = "", delay = 0 }) => {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          style={{ display: "inline-block", willChange: "transform, filter" }}
          initial={{ opacity: 0, filter: "blur(10px)", y: 12 }}
          whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: delay + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
        >
          {word}
          {i < words.length - 1 ? "\u00A0" : ""}
        </motion.span>
      ))}
    </span>
  );
};

/* ---------- ShinyText: sweeping shine across text (reactbits style) ---------- */
export const ShinyText = ({ children, className = "" }) => (
  <span className={`shiny-text ${className}`}>{children}</span>
);

/* ---------- Magnetic: element gravitates toward cursor (reactbits style) ---------- */
export const Magnetic = ({ children, strength = 0.35, className = "" }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 200, damping: 16 });
  const sy = useSpring(y, { stiffness: 200, damping: 16 });

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className={`inline-block ${className}`}>
      {children}
    </motion.div>
  );
};

/* ---------- Marquee: infinite scrolling strip (reactbits style) ---------- */
export const Marquee = ({ items, className = "" }) => (
  <div className={`marquee-mask relative overflow-hidden ${className}`}>
    <div className="marquee-track flex w-max items-center gap-0">
      {[0, 1].map((dup) => (
        <div key={dup} className="flex items-center" aria-hidden={dup === 1}>
          {items.map((item, i) => (
            <span key={`${dup}-${i}`} className="flex items-center whitespace-nowrap">
              <span className="font-heading text-sm font-semibold uppercase tracking-[0.2em] text-[#8A8272]">{item}</span>
              <svg width="14" height="16" viewBox="0 0 14 16" className="mx-8 shrink-0">
                <path d="M7 0.5 L13 4 L13 12 L7 15.5 L1 12 L1 4 Z" fill="#F0A322" fillOpacity="0.7" />
              </svg>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------- FloatingHexes: drifting honeycomb accents ---------- */
const HEX_SPOTS = [
  { left: "6%", top: "18%", size: 46, dur: 7, delay: 0, o: 0.35 },
  { left: "12%", top: "68%", size: 26, dur: 9, delay: 1.2, o: 0.5 },
  { left: "84%", top: "14%", size: 64, dur: 8, delay: 0.5, o: 0.4 },
  { left: "90%", top: "58%", size: 30, dur: 6, delay: 2, o: 0.55 },
  { left: "72%", top: "78%", size: 42, dur: 10, delay: 0.8, o: 0.3 },
  { left: "45%", top: "10%", size: 22, dur: 7.5, delay: 1.6, o: 0.45 },
];

export const FloatingHexes = ({ className = "" }) => (
  <div className={`pointer-events-none absolute inset-0 hidden lg:block ${className}`} aria-hidden="true">
    {HEX_SPOTS.map((h, i) => (
      <motion.svg
        key={i}
        width={h.size}
        height={h.size * 1.14}
        viewBox="0 0 64 72"
        fill="none"
        className="absolute"
        style={{ left: h.left, top: h.top, opacity: h.o }}
        animate={{ y: [0, -16, 0], rotate: [0, i % 2 ? 8 : -8, 0] }}
        transition={{ duration: h.dur, repeat: Infinity, ease: "easeInOut", delay: h.delay }}
      >
        <path
          d="M32 2 L60 18 L60 53 L32 70 L4 53 L4 18 Z"
          fill={i % 3 === 0 ? "#F0A322" : "none"}
          fillOpacity={i % 3 === 0 ? 0.12 : 0}
          stroke="#E8930C"
          strokeOpacity="0.5"
          strokeWidth="1.5"
        />
      </motion.svg>
    ))}
  </div>
);
